import React, { useMemo, useRef, useState } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import deLocale from "@fullcalendar/core/locales/de";
import { processColors } from "./constants";

// Convert template processes to calendar events
const convertTemplateToEvents = (template, startDate) => {
  if (!template || !template.processes) return [];

  const events = [];
  let currentDate = new Date(startDate);

  template.processes.forEach((process, index) => {
    const hours = process.hours || 1;
    const durationInDays = Math.ceil(hours / 8); // Assuming 8-hour workday

    const event = {
      id: `${template._id}-${process.id}-${index}`,
      title: process.label,
      start: new Date(currentDate),
      end: new Date(currentDate.getTime() + durationInDays * 24 * 60 * 60 * 1000),
      backgroundColor: processColors[process.id] || processColors[1],
      borderColor: processColors[process.id] || processColors[1],
      extendedProps: {
        templateId: template._id,
        templateName: template.name,
        processId: process.id,
        hours: hours,
        names: process.names || [],
        anr: template.assignedANR?.anr || null,
        auftragsbezeichnung: template.assignedANR?.auftragsbezeichnung || null,
      },
    };

    events.push(event);

    // Move to next day for sequential processes
    currentDate = new Date(currentDate.getTime() + durationInDays * 24 * 60 * 60 * 1000);
  });

  return events;
};

const TemplateCalendar = ({ selectedTemplate, onDateSelect }) => {
  const calendarRef = useRef(null);
  const [tooltipData, setTooltipData] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  // Generate events from selected template
  const events = useMemo(() => {
    if (!selectedTemplate) return [];
    // Start from today
    const startDate = new Date();
    startDate.setHours(0, 0, 0, 0);
    return convertTemplateToEvents(selectedTemplate, startDate);
  }, [selectedTemplate]);

  // Handle event click
  const handleEventClick = (info) => {
    const { extendedProps } = info.event;
    console.log("Event clicked:", extendedProps);
  };

  // Handle event mouse enter for tooltip
  const handleEventMouseEnter = (info) => {
    const { extendedProps } = info.event;
    const rect = info.el.getBoundingClientRect();

    setTooltipData({
      title: info.event.title,
      hours: extendedProps.hours,
      names: extendedProps.names,
      templateName: extendedProps.templateName,
      anr: extendedProps.anr,
      auftragsbezeichnung: extendedProps.auftragsbezeichnung,
      start: info.event.start,
      end: info.event.end,
    });

    setTooltipPosition({
      x: rect.left + rect.width / 2,
      y: rect.bottom + 10,
    });
  };

  // Handle event mouse leave
  const handleEventMouseLeave = () => {
    setTooltipData(null);
  };

  // Handle date selection
  const handleDateSelect = (selectInfo) => {
    if (onDateSelect) {
      onDateSelect({
        start: selectInfo.start,
        end: selectInfo.end,
        allDay: selectInfo.allDay,
      });
    }
  };

  return (
    <div className="h-full flex flex-col bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      {/* Calendar Header */}
      <div className="p-4 border-b border-gray-200 bg-gray-50">
        <h2 className="text-lg font-semibold text-gray-900">
          {selectedTemplate
            ? `Kalender: ${selectedTemplate.name}`
            : "Kalendervorschau"}
        </h2>
        {selectedTemplate?.assignedANR && (
          <p className="text-sm text-blue-600 mt-1">
            ANR: {selectedTemplate.assignedANR.anr} -{" "}
            {selectedTemplate.assignedANR.auftragsbezeichnung}
          </p>
        )}
        {!selectedTemplate && (
          <p className="text-sm text-gray-500 mt-1">
            Wählen Sie ein Template aus der Liste, um die Prozesse anzuzeigen
          </p>
        )}
      </div>

      {/* Calendar */}
      <div className="flex-1 p-4 overflow-auto calendar-container">
        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          locale={deLocale}
          headerToolbar={{
            left: "prev,next today",
            center: "title",
            right: "dayGridMonth,timeGridWeek",
          }}
          events={events}
          eventClick={handleEventClick}
          eventMouseEnter={handleEventMouseEnter}
          eventMouseLeave={handleEventMouseLeave}
          selectable={true}
          select={handleDateSelect}
          height="100%"
          dayMaxEvents={3}
          eventDisplay="block"
          eventTimeFormat={{
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
          }}
          slotMinTime="06:00:00"
          slotMaxTime="20:00:00"
          weekends={true}
          nowIndicator={true}
          editable={false}
          eventContent={(eventInfo) => (
            <div className="p-1 overflow-hidden">
              <div className="text-xs font-medium truncate text-white">
                {eventInfo.event.title}
              </div>
              {eventInfo.event.extendedProps.hours && (
                <div className="text-xs opacity-80 text-white">
                  {eventInfo.event.extendedProps.hours}h
                </div>
              )}
            </div>
          )}
        />
      </div>

      {/* Tooltip */}
      {tooltipData && (
        <div
          className="fixed z-50 bg-white rounded-lg shadow-xl border border-gray-200 p-4 max-w-xs"
          style={{
            left: `${tooltipPosition.x}px`,
            top: `${tooltipPosition.y}px`,
            transform: "translateX(-50%)",
          }}
        >
          <div className="font-semibold text-gray-900 mb-2">
            {tooltipData.title}
          </div>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Dauer:</span>
              <span className="font-medium">{tooltipData.hours} Stunden</span>
            </div>
            {tooltipData.anr && (
              <div className="flex justify-between">
                <span className="text-gray-500">ANR:</span>
                <span className="font-medium text-blue-600">
                  {tooltipData.anr}
                </span>
              </div>
            )}
            {tooltipData.names && tooltipData.names.length > 0 && (
              <div>
                <span className="text-gray-500">Zugewiesen:</span>
                <div className="flex flex-wrap gap-1 mt-1">
                  {tooltipData.names.map((name, idx) => (
                    <span
                      key={idx}
                      className="px-2 py-0.5 bg-gray-100 rounded text-xs"
                    >
                      {name}
                    </span>
                  ))}
                </div>
              </div>
            )}
            <div className="flex justify-between text-xs text-gray-400 pt-2 border-t mt-2">
              <span>
                {tooltipData.start?.toLocaleDateString("de-DE")}
              </span>
              <span>→</span>
              <span>
                {tooltipData.end?.toLocaleDateString("de-DE")}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!selectedTemplate && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/80 pointer-events-none">
          <div className="text-center">
            <svg
              className="w-16 h-16 mx-auto text-gray-300 mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <p className="text-gray-500">Kein Template ausgewählt</p>
          </div>
        </div>
      )}

      {/* Calendar Styles */}
      <style>{`
        .calendar-container .fc {
          font-family: inherit;
        }
        .calendar-container .fc-toolbar-title {
          font-size: 1.25rem !important;
          font-weight: 600;
        }
        .calendar-container .fc-button {
          background-color: #3b82f6 !important;
          border-color: #3b82f6 !important;
          font-weight: 500;
          padding: 0.5rem 1rem;
        }
        .calendar-container .fc-button:hover {
          background-color: #2563eb !important;
          border-color: #2563eb !important;
        }
        .calendar-container .fc-button-active {
          background-color: #1d4ed8 !important;
          border-color: #1d4ed8 !important;
        }
        .calendar-container .fc-event {
          cursor: pointer;
          border-radius: 4px;
          border: none !important;
        }
        .calendar-container .fc-daygrid-event {
          margin: 1px 2px;
        }
        .calendar-container .fc-day-today {
          background-color: #eff6ff !important;
        }
        .calendar-container .fc-highlight {
          background-color: #dbeafe !important;
        }
      `}</style>
    </div>
  );
};

export default TemplateCalendar;
